// Where every branch on a mind map goes, worked out from the branches alone.
//
// Pure: no React, no browser, no measurement, no clock - the same split
// `day/day-model.ts` and `month/month-model.ts` keep, and here it earns more
// than testability. A layout that measured the boxes it had drawn would have to
// draw them first, measure, and draw again, which is a visible reflow on every
// keystroke. This computes from the tree and the tree only, and the boxes are
// sized by CSS to fit inside what it reserved for them.
//
// A MAP ARRANGES ITSELF, AND THEN A PERSON MAY MOVE ANY OF IT. Both halves
// matter and the order of them is the design:
//
//   THE COMPUTED ARRANGEMENT IS WHAT A MAP IS BEFORE ANYBODY TOUCHES IT. A new
//   map arranges itself, a burst of branches typed into the composer arranges
//   itself, and somebody who never drags anything gets a readable map for ever
//   without knowing dragging exists. That is not a fallback; it is the default,
//   and it is what keeps this usable on a phone and from a keyboard - a map that
//   can ONLY be arranged by dragging cannot be made on either.
//
//   DRAGGING IS ADDITIVE. A branch carries `x` and `y` (migration 0007), null
//   until somebody moves it, and only a non-null pair overrides the arithmetic.
//   So the two arrangements are not two modes: they are the same map, with the
//   boxes somebody moved sitting where they were put and everything else still
//   looking after itself. Every drag has a keyboard equivalent, and `Tidy up`
//   clears the lot back to null.
//
// The captain asked for dragging on 2026-08-19, after seeing and accepting the
// computed-only version; this is that request added to it rather than in place
// of it.
//
// THE ARRANGEMENT IS RADIAL, and the three rules that keep it readable are worth
// naming because each looks like a magic number and none of them is:
//
//   A SUBTREE'S SHARE OF THE CIRCLE IS ITS LEAF COUNT. A branch with six things
//   under it needs six branches' worth of room; one with nothing needs one. Even
//   spacing puts a crowded subtree on top of itself while a bare one has a
//   quarter of the page.
//
//   EVERY LEAF GETS `minArc` OF ARC, AND THAT NUMBER IS DERIVED. It is the
//   smallest gap along a ring at which two boxes of that depth cannot touch at
//   ANY angle - worked out from their own half-width and half-height and from
//   `RX` below, rather than chosen by eye. `web/test/realize-model.test.ts`
//   checks every pair of boxes on a dozen shapes, and it found two collisions an
//   eyeballed number had left in.
//
//   THE ROOM IS MADE BY FANNING FIRST AND MOVING OUT SECOND. A crowded branch
//   spreads its children as wide as its own share of the circle allows; only
//   when that is spent does the generation sit further out. Fanning keeps the
//   map compact, and moving out is what stops a share that cannot stretch from
//   being a pile.

import { PALETTE } from '../edit/edit-model.ts';
import type { MindNode } from '../api/client.ts';
import {
  addExactDecimals,
  compareExactDecimals,
  divideExactDecimals,
  exactDecimal,
  midpointExactDecimals,
  multiplyExactDecimals,
  projectExactDecimal,
  ratioToExactMagnitude,
  subtractExactDecimals,
} from '../api/exact-decimal.ts';
import type { ExactDecimal } from '../api/exact-decimal.ts';

/** The id the centre is addressed by. It is `mind_map.topic`, not a node. */
export const CENTRE = 'centre';

/**
 * How much wider a ring is than it is tall.
 *
 * THE RINGS ARE ELLIPSES, AND THAT IS WHAT MAKES A RADIAL MAP OF TEXT WORK AT
 * ALL. A box of words is wide and short - up to 190px across and about 90 tall -
 * so on a CIRCULAR ring the gap that comfortably clears two boxes stacked
 * vertically leaves them overlapping side by side, and the radius that clears
 * them side by side throws the whole map half a screen apart. It was drawn
 * circular once, and a three-branch map put "Getting there" across the middle of
 * "Weekend in Lisbon".
 *
 * Stretching the ring horizontally spends the room where the boxes are wide and
 * keeps it where they are short, so both directions clear at one radius.
 *
 * IT IS AN INPUT TO EVERY DISTANCE BELOW rather than a look: `minArc` and
 * `minRadialGap` are solved FROM it and from `HALF_W`, so this number and those
 * are one set. Widening a box in the stylesheet without widening `HALF_W` here
 * brings the collision back, which is why `styles.css` says so at the
 * max-widths.
 */
const RX = 1.55;

/**
 * The narrowest a subtree fans, in radians.
 *
 * A branch with room to spare still keeps its children together: spread across a
 * whole third of the circle they read as three separate branches of the CENTRE
 * rather than as three things under one branch. So a fan starts here and widens
 * only as far as the children actually need - and never past the share the
 * parent was given, which is what keeps two subtrees out of each other.
 */
const MIN_FAN = 1.4;

/**
 * Half the room a box may take, by depth.
 *
 * THE `max-width` OF `.rznode.d0`, `.d1` AND `.d2` IN `styles.css`, halved, and
 * a height tall enough for the three lines those boxes clamp to. They are the
 * MAXIMA rather than measurements, which is what lets this file place a box
 * without drawing it first - it over-reserves for a short label, and that costs
 * a little air and never a clipped word.
 */
const HALF_W = [95, 76, 68];
const HALF_H = 46;

/** A little more than touching, so two boxes that just clear look like it. */
const AIR = 14;

const halfWidthAt = (depth: number): number => HALF_W[Math.min(depth, HALF_W.length - 1)]!;

/**
 * The smallest gap ALONG A RING at which two boxes of this depth cannot touch,
 * whatever angle the ring puts them at.
 *
 * DERIVED, and this is the whole of the derivation. Two boxes `A` apart along a
 * ring at angle `t` are `RX * A * sin t` apart sideways and `A * cos t` apart
 * vertically. They clear each other if EITHER beats the pair of half-widths or
 * the pair of half-heights - so the worst angle is the one where both are only
 * just short, and the smallest `A` that has no such angle is
 *
 *     A = hypot(2 * halfHeight, 2 * halfWidth / RX)
 *
 * At six o'clock that spends itself sideways, where the boxes are wide; at three
 * o'clock it spends itself vertically, where they are short. This is the number
 * an eyeballed `PITCH` got wrong twice - once at the centre and once inside a
 * five-child fan - and the reason the collision test is written pair-by-pair.
 */
const minArc = (depth: number): number =>
  Math.hypot(2 * HALF_H, (2 * halfWidthAt(depth)) / RX) + AIR;

/**
 * The smallest gap BETWEEN TWO RINGS at which a box and one of its children
 * cannot touch, whatever angle they are both at.
 *
 * The same derivation as `minArc`, with the two boxes' own half-widths rather
 * than one box's twice - because here the pair is a parent and a child, and the
 * middle child of a fan sits at its parent's own angle, straight out from it.
 * That is the pair the test caught last: a hand-picked 130 was four pixels short
 * of what the numbers above actually require, and a three-level map put "Top 1"
 * across "Mid 1.1".
 */
const minRadialGap = (depth: number): number =>
  Math.hypot(2 * HALF_H, (halfWidthAt(depth - 1) + halfWidthAt(depth)) / RX) + AIR;

/** Air between the outermost box and the edge of the page. */
const MARGIN = 28;

/** One branch, with the branches under it. Ordered as the server ordered them. */
export interface Branch {
  id: string;
  label: string;
  /** The server's word for what to draw, which an empty label needs. */
  heading: string;
  parentId: string | null;
  sortIndex: number;
  /** Where it was dragged to, or null when nobody has moved it. */
  x: number | null;
  y: number | null;
  positionSpace: 'model' | 'normalized';
  anchorX: ExactDecimal;
  anchorY: ExactDecimal;
  renderDeltaX: ExactDecimal;
  renderDeltaY: ExactDecimal;
  renderOriginX: ExactDecimal;
  renderOriginY: ExactDecimal;
  children: Branch[];
}

/**
 * A branch, placed.
 *
 * `x` and `y` are the CENTRE of its box, in page coordinates - so a box is drawn
 * by translating it half its own width and height, and the connector below is
 * drawn to the same point whatever size the box turns out to be.
 */
export interface Placed {
  id: string;
  /** What is stored, which is what a rename field is filled with. May be empty. */
  label: string;
  /** What the box DRAWS - the server's word for an empty one, never this client's. */
  heading: string;
  parentId: string | null;
  /** 0 is the centre, 1 a branch off it, 2 a branch off that. */
  depth: number;
  x: number;
  y: number;
  /** Its position among its siblings, for `aria-posinset`. 1-based. */
  position: number;
  /** How many siblings it is one of, for `aria-setsize`. */
  siblings: number;
  /**
   * The hue this branch and everything under it is drawn in, as the STORED
   * `#RRGGBB` - the light value, which `hueIn` turns into the dark counterpart
   * when the page is dark. Null on the centre, which wears the ink.
   *
   * Taken from the top-level branch's `sortIndex` and not from its place in the
   * array: deleting the second of five branches must not repaint the three after
   * it, which is a change to branches nobody touched.
   */
  colorHex: string | null;
  /** Whether anything hangs off it. Drawn as a quiet count on the box. */
  childCount: number;
  /**
   * SOMEBODY PUT THIS ONE HERE, rather than the arrangement working it out.
   *
   * The screen says so out loud - a box that was dragged carries a quiet marker
   * and the map offers to tidy up - because an arrangement somebody made by hand
   * and one a computer made look identical, and only one of them is theirs to
   * lose. Nothing about how a box is DRAWN depends on this beyond that marker.
   */
  placedByHand: boolean;
  manualPosition: PositionOffset | null;
}

/** One connector, already an SVG path. Drawn under the boxes. */
export interface Link {
  id: string;
  d: string;
  /** The hue of the branch it leads to, so a limb is one colour end to end. */
  colorHex: string | null;
  depth: number;
}

export interface Layout {
  /** The centre first, then every branch in depth-first order. */
  nodes: Placed[];
  links: Link[];
  width: number;
  height: number;
  /** Where the middle ended up, once everything was shifted onto the page. */
  centre: { x: number; y: number };
  /** Ids in the order Up and Down walk them: exactly `nodes`, as ids. */
  order: string[];
  /**
   * Somebody has moved at least one box on this map.
   *
   * What `Tidy up` is drawn from: a map nobody has arranged has nothing to tidy,
   * so offering the control there would be a button that does nothing to a map
   * that already looks like what it would do.
   */
  arrangedByHand: boolean;
  renderScale: number;
  renderOriginX: ExactDecimal;
  renderOriginY: ExactDecimal;
  renderSpan: ExactDecimal | null;
  projectionSpan: ExactDecimal | null;
}

/**
 * The branches as a tree.
 *
 * The server sends a flat list because a tree on the wire is a tree a second
 * client has to agree on; the ORDER is the server's, and this preserves it
 * rather than sorting again. A node whose parent is missing is dropped: the
 * server never sends one (no live branch has a deleted ancestor - see
 * `api/src/db/repositories/realize.ts`), and drawing it at the centre would put
 * a branch on a map it does not belong to if that invariant ever broke.
 */
export function treeOf(nodes: readonly MindNode[]): Branch[] {
  const byId = new Map<string, Branch>();
  for (const node of nodes) {
    byId.set(node.id, {
      id: node.id,
      label: node.label,
      heading: node.heading,
      parentId: node.parentId,
      sortIndex: node.sortIndex,
      x: node.x,
      y: node.y,
      positionSpace: node.positionSpace,
      anchorX: node.anchorX ?? exactDecimal(node.x ?? 0),
      anchorY: node.anchorY ?? exactDecimal(node.y ?? 0),
      renderDeltaX: node.renderDeltaX ?? '0',
      renderDeltaY: node.renderDeltaY ?? '0',
      renderOriginX: node.renderOriginX ?? '0',
      renderOriginY: node.renderOriginY ?? '0',
      children: [],
    });
  }

  const roots: Branch[] = [];
  for (const node of nodes) {
    const branch = byId.get(node.id);
    if (branch === undefined) continue;
    if (node.parentId === null) {
      roots.push(branch);
      continue;
    }
    byId.get(node.parentId)?.children.push(branch);
  }
  return roots;
}

/** How many things each subtree ends in, which is its share of the room. */
function leafCounts(roots: readonly Branch[]): Map<string, number> {
  const order: Branch[] = [];
  const waiting = [...roots];
  while (waiting.length > 0) {
    const branch = waiting.pop();
    if (branch === undefined) continue;
    order.push(branch);
    for (const child of branch.children) waiting.push(child);
  }

  const counts = new Map<string, number>();
  for (let index = order.length - 1; index >= 0; index -= 1) {
    const branch = order[index]!;
    let total = 0;
    for (const child of branch.children) total += counts.get(child.id) ?? 1;
    counts.set(branch.id, total === 0 ? 1 : total);
  }
  return counts;
}

/** The hue a top-level branch wears, from its own number rather than its place. */
export function hueFor(sortIndex: number): string {
  const hue = PALETTE[((sortIndex % PALETTE.length) + PALETTE.length) % PALETTE.length];
  return hue?.hex ?? PALETTE[0]!.hex;
}

/**
 * Where a distance and an angle put you, with the centre at the origin.
 *
 * The ring is an ELLIPSE - see `RX` - so `radius` is the vertical one and the
 * horizontal is that much wider. Every point on this map goes through here,
 * connectors' control points included, or a line would leave the ellipse its
 * two ends sit on.
 */
function boundedScale(value: number, factor: number): number {
  const result = value * factor;
  if (Number.isFinite(result)) return result;
  return Math.sign(value) * Math.sign(factor) * Number.MAX_VALUE;
}

function boundedHypot(x: number, y: number): number {
  const scale = Math.max(Math.abs(x), Math.abs(y));
  if (scale === 0) return 0;
  const result = scale * Math.hypot(x / scale, y / scale);
  return Number.isFinite(result) ? result : Number.MAX_VALUE;
}

function polar(radius: number, angle: number): { x: number; y: number } {
  return {
    x: boundedScale(radius, RX * Math.cos(angle)),
    y: boundedScale(radius, Math.sin(angle)),
  };
}

const MODEL_LINEAR_RANGE = 12_000_000;
const MODEL_RENDER_LIMIT = 16_000_000;
const NORMALIZED_LINEAR_RANGE = 32_000_000;
const NORMALIZED_RENDER_LIMIT = 40_000_000;
const AGGREGATE_RENDER_TARGET = 31_000_000;
const DEFAULT_RENDER_SPAN: ExactDecimal = '31000000';

function manualCoordinate(
  anchor: ExactDecimal,
  delta: ExactDecimal,
  origin: ExactDecimal,
  positionSpace: 'model' | 'normalized',
): { coarse: number; exactCoarse: ExactDecimal; fine: number; exactFine: ExactDecimal } {
  const exactCoarse =
    positionSpace === 'normalized'
      ? anchor
      : exactDecimal(projectExactDecimal(anchor, MODEL_LINEAR_RANGE, MODEL_RENDER_LIMIT));
  const exactFine = subtractExactDecimals(delta, origin);
  const project = (value: ExactDecimal): number =>
    projectExactDecimal(value, NORMALIZED_LINEAR_RANGE, NORMALIZED_RENDER_LIMIT);
  return {
    coarse: project(exactCoarse),
    exactCoarse,
    fine: project(exactFine),
    exactFine,
  };
}

const round = (n: number): number => {
  const scaled = n * 10;
  return Number.isFinite(scaled) ? Math.round(scaled) / 10 : n;
};

/**
 * The connector from one point to another, as an SVG path.
 *
 * From the CENTRE it is a straight line: a curve out of the middle of a mind map
 * reads as a mistake, because there is no direction for it to lean away from.
 * Everywhere else it is a cubic whose control points sit on the two ENDS' own
 * angles, part-way out - so the limb leaves its parent pointing the way the
 * parent points and arrives at the child pointing the way the child does, which
 * is what makes a branch read as growing rather than as a wire.
 */
function pathBetween(
  from: { radius: number; angle: number; x: number; y: number },
  to: { radius: number; angle: number; x: number; y: number },
  centre: { x: number; y: number },
): string {
  const head = `M ${round(from.x)} ${round(from.y)}`;
  const tail = `${round(to.x)} ${round(to.y)}`;
  if (from.radius === 0) return `${head} L ${tail}`;

  const span = to.radius - from.radius;
  const c1 = polar(from.radius + span * 0.35, from.angle);
  const c2 = polar(from.radius + span * 0.78, to.angle);
  const minX = Math.min(from.x, to.x);
  const maxX = Math.max(from.x, to.x);
  const minY = Math.min(from.y, to.y);
  const maxY = Math.max(from.y, to.y);
  const bounded = (point: { x: number; y: number }): { x: number; y: number } => ({
    x: Math.min(maxX, Math.max(minX, centre.x + point.x)),
    y: Math.min(maxY, Math.max(minY, centre.y + point.y)),
  });
  const first = bounded(c1);
  const second = bounded(c2);
  return (
    `${head} C ${round(first.x)} ${round(first.y)}` +
    ` ${round(second.x)} ${round(second.y)} ${tail}`
  );
}

interface Working extends Placed {
  radius: number;
  angle: number;
  coarseX: ExactDecimal;
  coarseY: ExactDecimal;
  fineX: ExactDecimal;
  fineY: ExactDecimal;
}

/**
 * The whole arrangement: every box, every connector, and how big the page is.
 *
 * The centre goes in at depth 0 whether or not anything hangs off it, because an
 * empty map is still a topic in the middle - and the screen it is drawn on
 * invites the first branch rather than showing a blank.
 */
export function layout(
  /** The thing in the middle: what is stored, and what is drawn for it. */
  centreOf: { topic: string; heading: string },
  nodes: readonly MindNode[],
  persistedRenderScale: number | null = null,
  persistedRenderOriginX: ExactDecimal = '0',
  persistedRenderOriginY: ExactDecimal = '0',
  persistedRenderSpan: ExactDecimal | null = null,
): Layout {
  const roots = treeOf(nodes);
  const leaves = leafCounts(roots);
  const centreNode: Working = {
    id: CENTRE,
    label: centreOf.topic,
    heading: centreOf.heading,
    parentId: null,
    depth: 0,
    x: 0,
    y: 0,
    position: 1,
    siblings: 1,
    colorHex: null,
    childCount: roots.length,
    placedByHand: false,
    manualPosition: null,
    radius: 0,
    angle: 0,
    coarseX: '0',
    coarseY: '0',
    fineX: '0',
    fineY: '0',
  };

  interface Generation {
    children: readonly Branch[];
    parent: Working;
    from: number;
    to: number;
    depth: number;
  }

  const generated = new Map<string, Working>();
  const generations: Generation[] = [
    {
      children: roots,
      parent: centreNode,
      from: -Math.PI / 2,
      to: -Math.PI / 2 + 2 * Math.PI,
      depth: 1,
    },
  ];

  while (generations.length > 0) {
    const generation = generations.pop();
    if (generation === undefined || generation.children.length === 0) continue;
    const { children, parent, from, to, depth } = generation;
    const total = children.reduce((sum, child) => sum + (leaves.get(child.id) ?? 1), 0) || 1;
    const available = to - from;
    const needed = total * minArc(depth);
    const nearest = Math.min(Number.MAX_VALUE, parent.radius + minRadialGap(depth));
    const fan = depth === 1 ? available : Math.min(available, Math.max(MIN_FAN, needed / nearest));
    const start = depth === 1 ? from : parent.angle - fan / 2;
    const radius = Math.max(nearest, needed / fan);

    let cursor = start;
    const next: Generation[] = [];
    children.forEach((child, index) => {
      const share = ((leaves.get(child.id) ?? 1) / total) * fan;
      const angle = cursor + share / 2;
      const computed = polar(radius, angle);
      const dragged =
        typeof child.x === 'number' &&
        typeof child.y === 'number' &&
        Number.isFinite(child.x) &&
        Number.isFinite(child.y);
      const manualPosition: PositionOffset | null = dragged
        ? {
            x: child.x as number,
            y: child.y as number,
            positionSpace: child.positionSpace,
            anchorX: child.anchorX,
            anchorY: child.anchorY,
            renderDeltaX: child.renderDeltaX,
            renderDeltaY: child.renderDeltaY,
            renderOriginX: child.renderOriginX,
            renderOriginY: child.renderOriginY,
          }
        : null;
      const manualX =
        manualPosition === null
          ? null
          : manualCoordinate(
              manualPosition.anchorX,
              manualPosition.renderDeltaX,
              manualPosition.renderOriginX,
              manualPosition.positionSpace,
            );
      const manualY =
        manualPosition === null
          ? null
          : manualCoordinate(
              manualPosition.anchorY,
              manualPosition.renderDeltaY,
              manualPosition.renderOriginY,
              manualPosition.positionSpace,
            );
      const at =
        manualX === null || manualY === null
          ? computed
          : { x: manualX.coarse + manualX.fine, y: manualY.coarse + manualY.fine };
      const node: Working = {
        id: child.id,
        label: child.label,
        heading: child.heading,
        parentId: child.parentId,
        depth,
        x: at.x,
        y: at.y,
        position: index + 1,
        siblings: children.length,
        colorHex: depth === 1 ? hueFor(child.sortIndex) : parent.colorHex,
        childCount: child.children.length,
        placedByHand: dragged,
        manualPosition,
        radius: dragged ? boundedHypot(at.x / RX, at.y) : radius,
        angle: dragged ? Math.atan2(at.y, at.x / RX) : angle,
        coarseX: manualX?.exactCoarse ?? exactDecimal(at.x),
        coarseY: manualY?.exactCoarse ?? exactDecimal(at.y),
        fineX: manualX?.exactFine ?? '0',
        fineY: manualY?.exactFine ?? '0',
      };
      generated.set(child.id, node);
      next.push({
        children: child.children,
        parent: node,
        from: cursor,
        to: cursor + share,
        depth: depth + 1,
      });
      cursor += share;
    });
    for (let index = next.length - 1; index >= 0; index -= 1) generations.push(next[index]!);
  }

  const placed: Working[] = [centreNode];
  const ordered = [...roots].reverse();
  while (ordered.length > 0) {
    const branch = ordered.pop();
    if (branch === undefined) continue;
    const node = generated.get(branch.id);
    if (node !== undefined) placed.push(node);
    for (let index = branch.children.length - 1; index >= 0; index -= 1) {
      ordered.push(branch.children[index]!);
    }
  }

  let minimumX: ExactDecimal = '0';
  let minimumY: ExactDecimal = '0';
  let maximumX: ExactDecimal = '0';
  let maximumY: ExactDecimal = '0';
  let manualMinimumX: ExactDecimal | null = null;
  let manualMinimumY: ExactDecimal | null = null;
  let manualMaximumX: ExactDecimal | null = null;
  let manualMaximumY: ExactDecimal | null = null;
  let hasManualPosition = false;
  for (const node of placed) {
    if (!node.placedByHand) continue;
    hasManualPosition = true;
    const worldX = addExactDecimals(node.coarseX, node.fineX);
    const worldY = addExactDecimals(node.coarseY, node.fineY);
    if (compareExactDecimals(worldX, minimumX) < 0) minimumX = worldX;
    if (compareExactDecimals(worldY, minimumY) < 0) minimumY = worldY;
    if (compareExactDecimals(worldX, maximumX) > 0) maximumX = worldX;
    if (compareExactDecimals(worldY, maximumY) > 0) maximumY = worldY;
    if (manualMinimumX === null || compareExactDecimals(worldX, manualMinimumX) < 0) {
      manualMinimumX = worldX;
    }
    if (manualMinimumY === null || compareExactDecimals(worldY, manualMinimumY) < 0) {
      manualMinimumY = worldY;
    }
    if (manualMaximumX === null || compareExactDecimals(worldX, manualMaximumX) > 0) {
      manualMaximumX = worldX;
    }
    if (manualMaximumY === null || compareExactDecimals(worldY, manualMaximumY) > 0) {
      manualMaximumY = worldY;
    }
  }
  const spanX = subtractExactDecimals(maximumX, minimumX);
  const spanY = subtractExactDecimals(maximumY, minimumY);
  const worldSpan = compareExactDecimals(spanX, spanY) >= 0 ? spanX : spanY;
  const hasPersistedFrame =
    persistedRenderScale !== null &&
    Number.isFinite(persistedRenderScale) &&
    persistedRenderScale > 0 &&
    persistedRenderScale <= 1;
  let renderScale = hasManualPosition && hasPersistedFrame ? persistedRenderScale : 1;
  let renderOriginX = hasManualPosition ? persistedRenderOriginX : '0';
  let renderOriginY = hasManualPosition ? persistedRenderOriginY : '0';
  if (
    hasManualPosition &&
    !hasPersistedFrame &&
    compareExactDecimals(
      multiplyExactDecimals(worldSpan, exactDecimal(renderScale)),
      exactDecimal(AGGREGATE_RENDER_TARGET),
    ) > 0
  ) {
    renderScale = Math.min(renderScale, ratioToExactMagnitude(AGGREGATE_RENDER_TARGET, worldSpan));
    renderOriginX = midpointExactDecimals(manualMinimumX ?? '0', manualMaximumX ?? '0');
    renderOriginY = midpointExactDecimals(manualMinimumY ?? '0', manualMaximumY ?? '0');
  }
  const exactScale = exactDecimal(renderScale);
  const projectedOrigin = (origin: ExactDecimal): ExactDecimal =>
    exactDecimal(projectExactDecimal(origin, MODEL_LINEAR_RANGE, MODEL_RENDER_LIMIT));
  const framed = (coarse: ExactDecimal, fine: ExactDecimal, origin: ExactDecimal): ExactDecimal =>
    addExactDecimals(
      projectedOrigin(origin),
      multiplyExactDecimals(
        subtractExactDecimals(addExactDecimals(coarse, fine), origin),
        exactScale,
      ),
    );

  let framedMinimumX: ExactDecimal = '0';
  let framedMinimumY: ExactDecimal = '0';
  let framedMaximumX: ExactDecimal = '0';
  let framedMaximumY: ExactDecimal = '0';
  for (const node of placed) {
    if (!node.placedByHand) continue;
    const x = framed(node.coarseX, node.fineX, renderOriginX);
    const y = framed(node.coarseY, node.fineY, renderOriginY);
    if (compareExactDecimals(x, framedMinimumX) < 0) framedMinimumX = x;
    if (compareExactDecimals(y, framedMinimumY) < 0) framedMinimumY = y;
    if (compareExactDecimals(x, framedMaximumX) > 0) framedMaximumX = x;
    if (compareExactDecimals(y, framedMaximumY) > 0) framedMaximumY = y;
  }
  const magnitudeOf = (value: ExactDecimal): ExactDecimal =>
    value.startsWith('-') ? subtractExactDecimals('0', value) : value;
  let framedMagnitude: ExactDecimal = '0';
  for (const edge of [framedMinimumX, framedMinimumY, framedMaximumX, framedMaximumY]) {
    const magnitude = magnitudeOf(edge);
    if (compareExactDecimals(magnitude, framedMagnitude) > 0) framedMagnitude = magnitude;
  }
  const initialRenderSpan =
    compareExactDecimals(framedMagnitude, DEFAULT_RENDER_SPAN) > 0
      ? multiplyExactDecimals(framedMagnitude, '2')
      : DEFAULT_RENDER_SPAN;
  const renderSpan = hasManualPosition
    ? persistedRenderSpan === null ||
      compareExactDecimals(multiplyExactDecimals(persistedRenderSpan, '2'), initialRenderSpan) < 0
      ? initialRenderSpan
      : persistedRenderSpan
    : null;
  const frameCoordinate = (
    coarse: ExactDecimal,
    fine: ExactDecimal,
    origin: ExactDecimal,
  ): number => {
    const coordinate = framed(coarse, fine, origin);
    return (
      Number(divideExactDecimals(coordinate, renderSpan ?? DEFAULT_RENDER_SPAN, 24)) *
      AGGREGATE_RENDER_TARGET
    );
  };

  const displacement = new Map<string, { x: number; y: number }>();
  displacement.set(CENTRE, { x: 0, y: 0 });
  for (const node of placed) {
    if (node.id === CENTRE) continue;
    const raw = { x: node.x, y: node.y };
    if (node.placedByHand) {
      node.x = frameCoordinate(node.coarseX, node.fineX, renderOriginX);
      node.y = frameCoordinate(node.coarseY, node.fineY, renderOriginY);
    } else {
      const inherited = displacement.get(node.parentId ?? CENTRE) ?? { x: 0, y: 0 };
      node.x += inherited.x;
      node.y += inherited.y;
    }
    displacement.set(node.id, { x: node.x - raw.x, y: node.y - raw.y });
    node.radius = boundedHypot(node.x / RX, node.y);
    node.angle = Math.atan2(node.y, node.x / RX);
  }

  // Shift everything onto a page that starts at 0,0, reserving the room a box of
  // that depth may take. The half-widths are the CSS maxima, so this over-
  // reserves for a short label - which costs a little air and never a clipped
  // word.
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const node of placed) {
    minX = Math.min(minX, node.x - halfWidthAt(node.depth));
    maxX = Math.max(maxX, node.x + halfWidthAt(node.depth));
    minY = Math.min(minY, node.y - HALF_H);
    maxY = Math.max(maxY, node.y + HALF_H);
  }

  const dx = MARGIN - minX;
  const dy = MARGIN - minY;
  for (const node of placed) {
    node.x = round(node.x + dx);
    node.y = round(node.y + dy);
  }

  const centre = { x: placed[0]!.x, y: placed[0]!.y };
  const byId = new Map(placed.map((node) => [node.id, node]));

  const links: Link[] = [];
  for (const node of placed) {
    if (node.depth === 0) continue;
    const parent = node.parentId === null ? placed[0]! : byId.get(node.parentId);
    if (parent === undefined) continue;
    links.push({
      id: node.id,
      d: pathBetween(parent, node, centre),
      colorHex: node.colorHex,
      depth: node.depth,
    });
  }

  return {
    nodes: placed.map(
      ({
        radius: _radius,
        angle: _angle,
        coarseX: _coarseX,
        coarseY: _coarseY,
        fineX: _fineX,
        fineY: _fineY,
        ...rest
      }) => rest,
    ),
    arrangedByHand: placed.some((node) => node.placedByHand),
    renderScale,
    renderOriginX,
    renderOriginY,
    renderSpan,
    projectionSpan: renderSpan,
    links,
    width: round(maxX - minX + MARGIN * 2),
    height: round(maxY - minY + MARGIN * 2),
    centre,
    order: placed.map((node) => node.id),
  };
}

/**
 * How far one press of an arrow key moves a box, in layout units.
 *
 * Big enough to be worth pressing and small enough to place a box deliberately.
 * Shift makes it `NUDGE_FAR`, for crossing a map without holding a key down -
 * the same pair of gears every drawing tool offers, because the alternative is
 * forty presses.
 */
export const NUDGE = 16;
export const NUDGE_FAR = 64;

/**
 * WHERE A KEY PRESS PUTS A BOX - the keyboard's half of dragging.
 *
 * The requirement this exists for: every arrangement action reachable by drag
 * has a keyboard path to the SAME result. Not an approximation of dragging and
 * not a different feature - it writes the same position representation the pointer
 * writes, so a map arranged with the arrow keys and one arranged with a mouse are the
 * same map and neither can tell which was used.
 *
 * A box that has never been moved starts from where the arrangement put it,
 * which is what makes the first press behave: nudging from `0, 0` would fling
 * it to the centre and then move it a pixel.
 *
 * Answers null when the key is not one of the four, so the caller leaves the
 * event alone.
 */
export interface PositionOffset {
  x: number;
  y: number;
  positionSpace: 'model' | 'normalized';
  anchorX: ExactDecimal;
  anchorY: ExactDecimal;
  renderDeltaX: ExactDecimal;
  renderDeltaY: ExactDecimal;
  renderOriginX: ExactDecimal;
  renderOriginY: ExactDecimal;
}

export interface PositionWrite extends PositionOffset {
  renderScale: number;
  mapRenderOriginX: ExactDecimal;
  mapRenderOriginY: ExactDecimal;
  mapRenderSpan: ExactDecimal;
}

export function nudgeTo(
  placed: Placed,
  centre: { x: number; y: number },
  key: string,
  far: boolean,
  renderScale = 1,
  renderOriginX: ExactDecimal = '0',
  renderOriginY: ExactDecimal = '0',
  renderSpan: ExactDecimal | null = null,
): PositionWrite | null {
  const step = far ? NUDGE_FAR : NUDGE;
  const from = renderedOffsetFromCentre(placed, centre);
  let to: { x: number; y: number };
  switch (key) {
    case 'ArrowLeft':
      to = { x: from.x - step, y: from.y };
      break;
    case 'ArrowRight':
      to = { x: from.x + step, y: from.y };
      break;
    case 'ArrowUp':
      to = { x: from.x, y: from.y - step };
      break;
    case 'ArrowDown':
      to = { x: from.x, y: from.y + step };
      break;
    default:
      return null;
  }
  return positionAt(placed, centre, to, renderScale, renderOriginX, renderOriginY, renderSpan);
}

/**
 * A point on the PAGE, as the offset from the centre that gets stored.
 *
 * The one conversion between what a pointer reports and what the database holds,
 * written once so a drag and a nudge cannot disagree about what a position means
 * (migration 0007: offsets from the centre, never page coordinates).
 */
export function renderedOffsetFromCentre(
  point: { x: number; y: number },
  centre: { x: number; y: number },
): { x: number; y: number } {
  return { x: round(point.x - centre.x), y: round(point.y - centre.y) };
}

export function positionAt(
  placed: Placed,
  centre: { x: number; y: number },
  target: { x: number; y: number },
  renderScale = 1,
  mapRenderOriginX: ExactDecimal = '0',
  mapRenderOriginY: ExactDecimal = '0',
  renderSpan: ExactDecimal | null = null,
): PositionWrite {
  const current = renderedOffsetFromCentre(placed, centre);
  const movement = { x: target.x - current.x, y: target.y - current.y };
  const exactScale = exactDecimal(renderScale);
  const currentSpan = renderSpan ?? DEFAULT_RENDER_SPAN;
  const targetMagnitude = Math.max(Math.abs(target.x), Math.abs(target.y));
  const growth =
    targetMagnitude <= AGGREGATE_RENDER_TARGET
      ? 1
      : Math.max(2, Math.ceil((targetMagnitude / AGGREGATE_RENDER_TARGET) * 2));
  const nextSpan =
    growth === 1 ? currentSpan : multiplyExactDecimals(currentSpan, exactDecimal(growth));
  const unproject = (pixels: number, span = nextSpan): ExactDecimal =>
    divideExactDecimals(
      multiplyExactDecimals(exactDecimal(round(pixels)), span),
      exactDecimal(AGGREGATE_RENDER_TARGET),
      24,
    );
  const projectedFrameOrigin = (origin: ExactDecimal): ExactDecimal =>
    exactDecimal(projectExactDecimal(origin, MODEL_LINEAR_RANGE, MODEL_RENDER_LIMIT));
  const shiftedAnchor = (origin: ExactDecimal, pixels: number): ExactDecimal =>
    addExactDecimals(
      origin,
      divideExactDecimals(
        subtractExactDecimals(unproject(pixels), projectedFrameOrigin(origin)),
        exactScale,
        24,
      ),
    );
  if (placed.manualPosition !== null && nextSpan === currentSpan) {
    const movedX = round(movement.x);
    const movedY = round(movement.y);
    const scaledMovementX = divideExactDecimals(unproject(movedX, currentSpan), exactScale, 24);
    const scaledMovementY = divideExactDecimals(unproject(movedY, currentSpan), exactScale, 24);
    const renderDeltaX = addExactDecimals(placed.manualPosition.renderDeltaX, scaledMovementX);
    const renderDeltaY = addExactDecimals(placed.manualPosition.renderDeltaY, scaledMovementY);
    const approximateX = placed.manualPosition.x + movedX;
    const approximateY = placed.manualPosition.y + movedY;
    return {
      ...placed.manualPosition,
      x: Number.isFinite(approximateX) ? approximateX : placed.manualPosition.x,
      y: Number.isFinite(approximateY) ? approximateY : placed.manualPosition.y,
      positionSpace: 'normalized',
      renderDeltaX,
      renderDeltaY,
      renderScale,
      mapRenderOriginX,
      mapRenderOriginY,
      mapRenderSpan: nextSpan,
    };
  }
  return {
    x: round(target.x),
    y: round(target.y),
    positionSpace: 'normalized',
    anchorX: shiftedAnchor(mapRenderOriginX, target.x),
    anchorY: shiftedAnchor(mapRenderOriginY, target.y),
    renderDeltaX: '0',
    renderDeltaY: '0',
    renderOriginX: '0',
    renderOriginY: '0',
    renderScale,
    mapRenderOriginX,
    mapRenderOriginY,
    mapRenderSpan: nextSpan,
  };
}

export function offsetFromCentre(
  point: { x: number; y: number },
  centre: { x: number; y: number },
): PositionOffset {
  const offset = renderedOffsetFromCentre(point, centre);
  return {
    x: offset.x,
    y: offset.y,
    positionSpace: 'normalized',
    anchorX: exactDecimal(offset.x),
    anchorY: exactDecimal(offset.y),
    renderDeltaX: '0',
    renderDeltaY: '0',
    renderOriginX: '0',
    renderOriginY: '0',
  };
}

/**
 * Every box somebody has moved, in stored centre-relative coordinates.
 *
 * The screen counts these when it describes what `Tidy up` changed.
 * `placedByHand` is exactly the branches with a stored pair.
 */
export function arrangementOf(plan: Layout): (PositionOffset & { id: string })[] {
  return plan.nodes
    .filter((node) => node.placedByHand)
    .flatMap((node) =>
      node.manualPosition === null ? [] : [{ id: node.id, ...node.manualPosition }],
    );
}

/**
 * Where a key press moves the focus, given where it is now.
 *
 * A mind map is a tree, so it takes the tree's own keys rather than a tab stop
 * per box: Up and Down walk the whole thing in reading order, Right goes in and
 * Left comes back out. That is what `role="tree"` promises, and a tree that
 * promised it and answered nothing would be worse than a plain list.
 *
 * Answers null when the key means nothing here - the caller then leaves the
 * event alone, so Left on the centre still does whatever the browser does.
 */
export function moveFocus(layout: Layout, from: string, key: string): string | null {
  const index = layout.order.indexOf(from);
  if (index === -1) return null;

  switch (key) {
    case 'ArrowDown':
      return layout.order[index + 1] ?? null;
    case 'ArrowUp':
      return layout.order[index - 1] ?? null;
    case 'Home':
      return layout.order[0] ?? null;
    case 'End':
      return layout.order[layout.order.length - 1] ?? null;
    case 'ArrowRight': {
      // The first thing under this one, which depth-first order puts next.
      const here = layout.nodes[index];
      if (here === undefined || here.childCount === 0) return null;
      return layout.order[index + 1] ?? null;
    }
    case 'ArrowLeft': {
      const here = layout.nodes[index];
      if (here === undefined || here.depth === 0) return null;
      return here.parentId ?? CENTRE;
    }
    default:
      return null;
  }
}
